'use client';

import { useEffect, useState } from 'react';

interface ElevatorAnimationProps {
  side: 'left' | 'right';
}

const ElevatorAnimation = ({ side }: ElevatorAnimationProps) => {
  const [animationState, setAnimationState] = useState(0);

  useEffect(() => {
    // Slower timing for more deliberate animation - 4 seconds per state
    const interval = setInterval(() => {
      setAnimationState((prev) => (prev + 1) % 16); // 16 states for complete cycle
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  // Comprehensive animation sequence
  const getAnimationState = () => {
    switch (animationState) {
      // Bottom floor - left to right journey
      case 0: // Left elevator opens
        return { leftFloor: 'bottom', rightFloor: 'bottom', leftDoor: 'open', rightDoor: 'closed', character: 'inside-left' };
      case 1: // Character exits left, starts floating right
        return { leftFloor: 'bottom', rightFloor: 'bottom', leftDoor: 'open', rightDoor: 'closed', character: 'floating-left-to-right-bottom' };
      case 2: // Character continues floating across
        return { leftFloor: 'bottom', rightFloor: 'bottom', leftDoor: 'closed', rightDoor: 'closed', character: 'floating-left-to-right-bottom' };
      case 3: // Character reaches right elevator
        return { leftFloor: 'bottom', rightFloor: 'bottom', leftDoor: 'closed', rightDoor: 'open', character: 'entering-right-bottom' };
      case 4: // Right elevator doors close, moves up to middle
        return { leftFloor: 'bottom', rightFloor: 'middle', leftDoor: 'closed', rightDoor: 'closed', character: 'inside-right' };
      
      // Middle floor - right to left journey
      case 5: // Right elevator opens at middle
        return { leftFloor: 'bottom', rightFloor: 'middle', leftDoor: 'closed', rightDoor: 'open', character: 'inside-right' };
      case 6: // Character exits right, starts floating left
        return { leftFloor: 'bottom', rightFloor: 'middle', leftDoor: 'closed', rightDoor: 'open', character: 'floating-right-to-left-middle' };
      case 7: // Character continues floating across
        return { leftFloor: 'middle', rightFloor: 'middle', leftDoor: 'closed', rightDoor: 'closed', character: 'floating-right-to-left-middle' };
      case 8: // Character reaches left elevator
        return { leftFloor: 'middle', rightFloor: 'middle', leftDoor: 'open', rightDoor: 'closed', character: 'entering-left-middle' };
      case 9: // Left elevator doors close, moves up to top
        return { leftFloor: 'top', rightFloor: 'middle', leftDoor: 'closed', rightDoor: 'closed', character: 'inside-left' };
      
      // Top floor - left to right journey
      case 10: // Left elevator opens at top
        return { leftFloor: 'top', rightFloor: 'middle', leftDoor: 'open', rightDoor: 'closed', character: 'inside-left' };
      case 11: // Character exits left, starts floating right
        return { leftFloor: 'top', rightFloor: 'middle', leftDoor: 'open', rightDoor: 'closed', character: 'floating-left-to-right-top' };
      case 12: // Character continues floating across
        return { leftFloor: 'top', rightFloor: 'top', leftDoor: 'closed', rightDoor: 'closed', character: 'floating-left-to-right-top' };
      case 13: // Character reaches right elevator
        return { leftFloor: 'top', rightFloor: 'top', leftDoor: 'closed', rightDoor: 'open', character: 'entering-right-top' };
      case 14: // Right elevator doors close, moves down to bottom
        return { leftFloor: 'top', rightFloor: 'bottom', leftDoor: 'closed', rightDoor: 'closed', character: 'inside-right' };
      case 15: // Right elevator reaches bottom, prepares for next cycle
        return { leftFloor: 'bottom', rightFloor: 'bottom', leftDoor: 'closed', rightDoor: 'closed', character: 'inside-right' };
      
      default:
        return { leftFloor: 'bottom', rightFloor: 'bottom', leftDoor: 'closed', rightDoor: 'closed', character: 'inside-left' };
    }
  };

  const state = getAnimationState();

  // Floor positioning
  const floorPositions = {
    top: 'top-[10%]',
    middle: 'top-[45%]',
    bottom: 'top-[80%]',
  };

  // Character horizontal position during floating
  const getCharacterPosition = () => {
    const basePositions = {
      'exiting-left': 'left-[10%]',
      'floating-across-bottom': 'left-[50%] -translate-x-1/2',
      'floating-across-middle': 'left-[50%] -translate-x-1/2',
      'floating-across-top': 'left-[50%] -translate-x-1/2',
      'entering-right': 'left-[90%]',
      'hidden': 'left-[10%] opacity-0',
    };
    return basePositions[state.character as keyof typeof basePositions] || '';
  };

  // Character vertical position matches the current floor
  const getCharacterFloor = () => {
    if (state.character.includes('bottom')) return floorPositions.bottom;
    if (state.character.includes('middle')) return floorPositions.middle;
    if (state.character.includes('top')) return floorPositions.top;
    if (state.character === 'exiting-left' || state.character === 'entering-right') {
      return floorPositions[state.leftFloor as keyof typeof floorPositions];
    }
    return floorPositions.bottom;
  };

  // Only render elevator on the appropriate side
  if (side === 'left') {
    return (
      <div className="absolute left-4 top-0 h-full w-28 pointer-events-none">
        {/* Elevator shaft indicator lines */}
        <div className="absolute left-1/2 top-[10%] bottom-[15%] w-0.5 bg-gray-300/30 -translate-x-1/2" />
        
        {/* Left Elevator at current floor */}
        <div
          className={`absolute ${floorPositions[state.leftFloor as keyof typeof floorPositions]} left-1/2 -translate-x-1/2 transition-all duration-[2000ms] ease-in-out`}
        >
          {/* Elevator box - 15% taller */}
          <div className="relative w-[97px] h-32 bg-red-500 border-[3px] border-black rounded-sm shadow-lg">
            {/* Elevator doors */}
            <div className="absolute inset-0 flex">
              <div
                className={`w-1/2 h-full bg-gray-700 border-r border-black transition-transform duration-[1500ms] ${
                  state.leftDoor === 'open' ? '-translate-x-full' : 'translate-x-0'
                }`}
              />
              <div
                className={`w-1/2 h-full bg-gray-700 border-l border-black transition-transform duration-[1500ms] ${
                  state.leftDoor === 'open' ? 'translate-x-full' : 'translate-x-0'
                }`}
              />
            </div>
            
            {/* Elevator window - 15% taller */}
            <div className="absolute top-3 left-1/2 -translate-x-1/2 w-[46px] h-[46px] bg-blue-300 border-2 border-black rounded-sm" />
          </div>
        </div>
      </div>
    );
  }

  // Right side elevator
  return (
    <div className="absolute right-4 top-0 h-full w-28 pointer-events-none">
      {/* Elevator shaft indicator lines */}
      <div className="absolute left-1/2 top-[10%] bottom-[15%] w-0.5 bg-gray-300/30 -translate-x-1/2" />
      
      {/* Right Elevator at current floor */}
      <div
        className={`absolute ${floorPositions[state.rightFloor as keyof typeof floorPositions]} left-1/2 -translate-x-1/2 transition-all duration-[2000ms] ease-in-out`}
      >
        {/* Elevator box - 15% taller */}
        <div className="relative w-[97px] h-32 bg-red-500 border-[3px] border-black rounded-sm shadow-lg">
          {/* Elevator doors */}
          <div className="absolute inset-0 flex">
            <div
              className={`w-1/2 h-full bg-gray-700 border-r border-black transition-transform duration-[1500ms] ${
                state.rightDoor === 'open' ? '-translate-x-full' : 'translate-x-0'
              }`}
            />
            <div
              className={`w-1/2 h-full bg-gray-700 border-l border-black transition-transform duration-[1500ms] ${
                state.rightDoor === 'open' ? 'translate-x-full' : 'translate-x-0'
              }`}
            />
          </div>
          
          {/* Elevator window - 15% taller */}
          <div className="absolute top-3 left-1/2 -translate-x-1/2 w-[46px] h-[46px] bg-blue-300 border-2 border-black rounded-sm" />
        </div>
      </div>
    </div>
  );
};

// Floating character component - rendered separately to span across both elevators
export const FloatingCharacter = () => {
  const [animationState, setAnimationState] = useState(0);

  useEffect(() => {
    // Match the elevator animation timing - 4 seconds per state
    const interval = setInterval(() => {
      setAnimationState((prev) => (prev + 1) % 16);
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  const getAnimationState = () => {
    switch (animationState) {
      // Bottom floor - left to right
      case 0:
        return { floor: 'bottom', position: 'inside-left', visible: false };
      case 1:
        return { floor: 'bottom', position: 'exiting-left', visible: true };
      case 2:
        return { floor: 'bottom', position: 'floating-across', visible: true };
      case 3:
        return { floor: 'bottom', position: 'entering-right', visible: true };
      case 4:
        return { floor: 'bottom', position: 'inside-right', visible: false };
      
      // Middle floor - right to left
      case 5:
        return { floor: 'middle', position: 'inside-right', visible: false };
      case 6:
        return { floor: 'middle', position: 'exiting-right', visible: true };
      case 7:
        return { floor: 'middle', position: 'floating-across-reverse', visible: true };
      case 8:
        return { floor: 'middle', position: 'entering-left', visible: true };
      case 9:
        return { floor: 'middle', position: 'inside-left', visible: false };
      
      // Top floor - left to right
      case 10:
        return { floor: 'top', position: 'inside-left', visible: false };
      case 11:
        return { floor: 'top', position: 'exiting-left', visible: true };
      case 12:
        return { floor: 'top', position: 'floating-across', visible: true };
      case 13:
        return { floor: 'top', position: 'entering-right', visible: true };
      case 14:
        return { floor: 'top', position: 'inside-right', visible: false };
      case 15:
        return { floor: 'bottom', position: 'inside-right', visible: false };
      
      default:
        return { floor: 'bottom', position: 'inside-left', visible: false };
    }
  };

  const state = getAnimationState();

  const floorPositions = {
    top: 'top-[10%]',
    middle: 'top-[45%]',
    bottom: 'top-[80%]',
  };

  const getCharacterHorizontalPosition = () => {
    switch (state.position) {
      case 'inside-left': case'exiting-left':
        return 'left-[8%]';
      case 'floating-across':
        return 'left-[50%] -translate-x-1/2';
      case 'entering-right': case'inside-right':
        return 'left-[92%]';
      case 'exiting-right':
        return 'left-[92%]';
      case 'floating-across-reverse':
        return 'left-[50%] -translate-x-1/2';
      case 'entering-left':
        return 'left-[8%]';
      default:
        return 'left-[8%]';
    }
  };

  // Don't render if character is inside elevator
  if (!state.visible) return null;

  return (
    <div
      className={`fixed ${floorPositions[state.floor as keyof typeof floorPositions]} ${getCharacterHorizontalPosition()} 
        transition-all duration-[3500ms] ease-in-out pointer-events-none z-40`}
    >
      {/* Warehouse Bob - User's exact SVG with mustache, name tag, and hat */}
      <svg 
        id="warehouseBob"
        width="75"
        height="100"
        viewBox="0 0 300 400"
        xmlns="http://www.w3.org/2000/svg"
        className="animate-bob"
      >
        {/* Face */}
        <circle cx="150" cy="110" r="70" fill="#F4D5B5" stroke="#000" strokeWidth="6"/>
        
        {/* Eyes */}
        <circle cx="125" cy="95" r="20" fill="#fff" stroke="#000" strokeWidth="4"/>
        <circle cx="175" cy="95" r="20" fill="#fff" stroke="#000" strokeWidth="4"/>
        <circle cx="125" cy="95" r="9" fill="#000"/>
        <circle cx="175" cy="95" r="9" fill="#000"/>
        
        {/* Mustache */}
        <path 
          d="M110 140 
             Q150 160 190 140 
             Q150 150 110 140 Z"
          fill="#3B2B22"
          stroke="#000"
          strokeWidth="4"
        />
        
        {/* Mouth */}
        <ellipse cx="150" cy="155" rx="22" ry="12" fill="#000"/>
        
        {/* Torso */}
        <rect x="80" y="170" width="140" height="120" fill="#007BFF" stroke="#000" strokeWidth="6" rx="10"/>
        
        {/* Name Text (Bob's actual left side – viewer's right) */}
        <text
          x="185"
          y="225"
          fontSize="20"
          fontFamily="Arial, sans-serif"
          fill="#ffffff"
          stroke="#000"
          strokeWidth="3"
          textAnchor="middle"
          style={{ fontWeight: 'bold' }}
        >
          Bob
        </text>
        
        {/* Arms */}
        <rect x="50" y="175" width="30" height="110" fill="#FF0000" stroke="#000" strokeWidth="6" rx="10"/>
        <rect x="220" y="175" width="30" height="110" fill="#FF0000" stroke="#000" strokeWidth="6" rx="10"/>
        
        {/* Hands */}
        <circle cx="65" cy="295" r="17" fill="#F4D5B5" stroke="#000" strokeWidth="5"/>
        <circle cx="235" cy="295" r="17" fill="#F4D5B5" stroke="#000" strokeWidth="5"/>
        
        {/* Legs */}
        <rect x="100" y="290" width="40" height="90" fill="#FFD400" stroke="#000" strokeWidth="6" rx="10"/>
        <rect x="160" y="290" width="40" height="90" fill="#FFD400" stroke="#000" strokeWidth="6" rx="10"/>
        
        {/* Shoes */}
        <rect x="95" y="370" width="50" height="22" fill="#000" stroke="#000" strokeWidth="5" rx="5"/>
        <rect x="155" y="370" width="50" height="22" fill="#000" stroke="#000" strokeWidth="5" rx="5"/>
        
        {/* Hat */}
        <rect x="95" y="35" width="110" height="25" fill="#FFD400" stroke="#000" strokeWidth="5" rx="4"/>
        <rect x="115" y="10" width="70" height="40" fill="#FFD400" stroke="#000" strokeWidth="5" rx="6"/>
      </svg>

      {/* Floating shadow */}
      <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-16 h-2 bg-black/20 rounded-full blur-sm" />
      
      {/* Motion lines when floating */}
      {(state.position === 'floating-across' || state.position === 'floating-across-reverse') && (
        <>
          <div className="absolute top-1/2 -left-4 w-3 h-0.5 bg-blue-400/60 rounded-full" />
          <div className="absolute top-1/2 -right-4 w-3 h-0.5 bg-blue-400/60 rounded-full" />
          <div className="absolute top-6 -left-3 w-2 h-2 bg-yellow-300 rounded-full animate-ping" />
          <div className="absolute bottom-6 -right-3 w-2 h-2 bg-yellow-300 rounded-full animate-ping" />
        </>
      )}
    </div>
  );
};

export default ElevatorAnimation;